import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Stack,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { InstitutionalHolder } from "../hooks/useInstitutionalOwnership";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatStockPrice } from "../../../../utils/stockPriceFormatter";
import { formatNumber } from "../utils/formatNumber";
import { panelTitleSx } from "../../overviewPanel";

const formatShares = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString() : "—";
const formatPercentage = (value: number) =>
  Number.isFinite(value) ? `${value.toFixed(2)}%` : "—";
const formatChange = (value: number) =>
  `${value > 0 ? "+" : ""}${formatShares(value)}`;
const formatPercentageChange = (value: number, digits = 2) => {
  if (!Number.isFinite(value)) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}%`;
};
const formatHoldingPeriod = (value: number) => {
  if (!Number.isFinite(value)) return "—";
  return `${value} ${value === 1 ? "quarter" : "quarters"}`;
};

interface InstitutionalOwnershipTableProps {
  holders: InstitutionalHolder[];
}

const InstitutionalOwnershipTable: React.FC<
  InstitutionalOwnershipTableProps
> = ({ holders }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currency, currencySymbol } = useStockCurrency();
  const topHolders = holders.slice(0, 15);
  const hairline = alpha(theme.palette.text.primary, 0.08);
  const wash = alpha(theme.palette.text.primary, 0.025);
  const formatMarketValue = (amount: number) =>
    Number.isFinite(amount) ? `${currencySymbol}${formatNumber(amount)}` : "—";
  const formatPrice = (price: number) =>
    Number.isFinite(price) ? formatStockPrice({ price, currency }) : "—";
  const changeColor = (value: number) => {
    const shade = theme.palette.mode === "dark" ? "main" : "dark";
    if (value > 0) return theme.palette.success[shade];
    if (value < 0) return theme.palette.error[shade];
    return theme.palette.text.secondary;
  };
  const investorName = (holder: InstitutionalHolder) => (
    <Box
      sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}
    >
      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        {holder.investorName}
      </Typography>
      {holder.isNew && (
        <Typography
          component="span"
          sx={{ fontSize: "0.625rem", fontWeight: 600, color: changeColor(1) }}
        >
          New
        </Typography>
      )}
    </Box>
  );

  if (topHolders.length === 0) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography
          component="h2"
          sx={{
            ...panelTitleSx,
            color: "text.primary",
            borderLeft: "2px solid",
            borderColor: "primary.main",
            pl: 1,
          }}
        >
          Top institutional holders
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: "0.6875rem",
            fontVariantNumeric: "tabular-nums",
            color: "text.secondary",
            bgcolor: "action.hover",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
          }}
        >
          {topHolders.length}
        </Typography>
      </Box>

      {!isMobile && (
        <TableContainer sx={{ bgcolor: "transparent" }}>
          <Table
            size="small"
            aria-label="Top institutional holders"
            sx={{
              "& .MuiTableCell-root": {
                borderBottom: `1px solid ${hairline}`,
                px: 1.5,
                py: 1.5,
                fontSize: "0.75rem",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              },
              "& .MuiTableCell-head": {
                bgcolor: "transparent",
                color: "text.secondary",
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                py: 1.25,
              },
              "& .holding-figure": {
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: "text.primary",
                letterSpacing: "-0.02em",
              },
              "& .holding-value": {
                bgcolor: wash,
                borderLeft: `1px solid ${hairline}`,
              },
              "& .MuiTableBody-root .holding-value": {
                fontSize: "1rem",
                fontWeight: 700,
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover": { bgcolor: wash },
              "& .holding-detail": {
                fontSize: "0.6875rem",
                fontWeight: 400,
                mt: 0.25,
                letterSpacing: "normal",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Investor</TableCell>
                <TableCell align="right">Held for</TableCell>
                <TableCell align="right">Ownership</TableCell>
                <TableCell align="right">Share change</TableCell>
                <TableCell align="right">Avg cost</TableCell>
                <TableCell align="right">Shares</TableCell>
                <TableCell align="right" className="holding-value">
                  Market value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topHolders.map((holder, index) => (
                <TableRow key={`${holder.cik}-${index}`}>
                  <TableCell sx={{ minWidth: 180, maxWidth: 300 }}>
                    {investorName(holder)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {formatHoldingPeriod(holder.holdingPeriod)}
                  </TableCell>
                  <TableCell align="right" className="holding-figure">
                    {formatPercentage(holder.ownership)}
                    {Number.isFinite(holder.changeInOwnership) &&
                      holder.changeInOwnership !== 0 && (
                        <Typography
                          className="holding-detail"
                          sx={{ color: changeColor(holder.changeInOwnership) }}
                        >
                          {formatPercentageChange(holder.changeInOwnership)}
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: changeColor(holder.changeInSharesNumber),
                      fontWeight: 500,
                    }}
                  >
                    {formatChange(holder.changeInSharesNumber)}
                    {Number.isFinite(holder.changeInSharesNumberPercentage) &&
                      holder.changeInSharesNumberPercentage !== 0 && (
                        <Typography
                          className="holding-detail"
                          sx={{
                            color: changeColor(
                              holder.changeInSharesNumberPercentage,
                            ),
                          }}
                        >
                          {formatPercentageChange(
                            holder.changeInSharesNumberPercentage,
                            1,
                          )}
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {formatPrice(holder.avgPricePaid)}
                  </TableCell>
                  <TableCell align="right" className="holding-figure">
                    {formatShares(holder.sharesNumber)}
                  </TableCell>
                  <TableCell
                    align="right"
                    className="holding-figure holding-value"
                  >
                    {formatMarketValue(holder.marketValue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isMobile && (
        <Stack>
          {topHolders.map((holder, index) => (
            <Box
              key={`${holder.cik}-${index}`}
              sx={{ px: 1.5, py: 2, borderBottom: `1px solid ${hairline}` }}
            >
              {investorName(holder)}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Held {formatHoldingPeriod(holder.holdingPeriod)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    ml: "auto",
                    color: changeColor(holder.changeInSharesNumber),
                  }}
                >
                  Share change {formatChange(holder.changeInSharesNumber)}
                  {Number.isFinite(holder.changeInSharesNumberPercentage) &&
                    holder.changeInSharesNumberPercentage !== 0 && (
                      <>
                        {" "}
                        (
                        {formatPercentageChange(
                          holder.changeInSharesNumberPercentage,
                          1,
                        )}
                        )
                      </>
                    )}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "1fr 1fr 1fr 1.3fr",
                  },
                  columnGap: 2,
                  rowGap: 1.5,
                  mt: 1.5,
                  fontVariantNumeric: "tabular-nums",
                  "& .amount-label": {
                    fontSize: "0.625rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "text.secondary",
                    mb: 0.5,
                  },
                  "& .amount-value": {
                    fontSize: "1rem",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    letterSpacing: "-0.025em",
                    overflowWrap: "anywhere",
                  },
                }}
              >
                <Box>
                  <Typography className="amount-label">Ownership</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatPercentage(holder.ownership)}
                  </Typography>
                  {Number.isFinite(holder.changeInOwnership) &&
                    holder.changeInOwnership !== 0 && (
                      <Typography
                        sx={{
                          fontSize: "0.6875rem",
                          mt: 0.5,
                          color: changeColor(holder.changeInOwnership),
                        }}
                      >
                        {formatPercentageChange(holder.changeInOwnership)}
                      </Typography>
                    )}
                </Box>
                <Box sx={{ textAlign: { xs: "right", sm: "left" } }}>
                  <Typography className="amount-label">Avg cost</Typography>
                  <Typography className="amount-value">
                    {formatPrice(holder.avgPricePaid)}
                  </Typography>
                </Box>
                <Box>
                  <Typography className="amount-label">Shares</Typography>
                  <Typography className="amount-value">
                    {formatShares(holder.sharesNumber)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography className="amount-label">Market value</Typography>
                  <Typography
                    className="amount-value"
                    sx={{ "&&": { fontSize: "1.375rem", fontWeight: 700 } }}
                  >
                    {formatMarketValue(holder.marketValue)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default InstitutionalOwnershipTable;
